import type { PostHog } from 'posthog-js';

import { getEnvironmentByHostname, isProduction } from '@/utils/environmentByHostname';

/** The part of the PostHog client that initialisation uses. */
export type PostHogInitClient = Pick<
    PostHog,
    'register' | 'has_opted_out_capturing' | 'opt_in_capturing' | 'opt_out_capturing'
> & {
    /** Absent on test doubles, which are treated as already loaded. */
    __loaded?: boolean;
    set_config?: (config: { capture_pageview?: boolean; capture_pageleave?: boolean }) => void;
};

let initialized = false;

/** Resets module state between Vitest runs. Not for production use. */
export function resetPostHogInitForTesting(): void {
    initialized = false;
}

/**
 * Registers the `environment` super property on every event, and opts out of
 * capturing outside production. Development and staging rely on the console
 * mirror in the dispatcher for visibility; only production sends to PostHog.
 * Safe to call multiple times — only runs once per page load.
 *
 * Returns false while the client has not finished loading, without sticking the
 * initialized flag, so the caller can retry after `PostHogProvider` inits.
 * `set_config` runs before opt-in: the library default `capture_pageview` is true,
 * and `opt_in_capturing` would otherwise emit a second `$pageview` beside ours.
 */
export function initializePostHogAnalytics(posthog: PostHogInitClient | undefined): boolean {
    if (!posthog) {
        return false;
    }

    if (initialized) {
        return true;
    }

    if (posthog.__loaded === false) {
        return false;
    }

    initialized = true;

    posthog.set_config?.({
        capture_pageview: false,
        capture_pageleave: false,
    });

    posthog.register({
        environment: getEnvironmentByHostname(),
    });

    if (isProduction()) {
        if (!posthog.has_opted_out_capturing()) {
            // Default options emit `$opt_in` immediately, which would land on the
            // pre-reset anonymous id. Consent is enough; the event is not.
            posthog.opt_in_capturing({ captureEventName: false });
        }
        return true;
    }

    posthog.opt_out_capturing();
    return true;
}
